import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { daysFromNow } from '@zapier/date';

import { calculateDiscount } from '@zapier/design-system-revenue/src/utils';
import { useIsAuthenticated } from '@zapier/design-system-revenue/src/hooks';

import { isEligibleForAnnualUpgrade } from './isEligibleForAnnualUpgrade';
import { annualBillingBanner as AnnualBillingBannerQuery } from './types/annualBillingBanner';

export const annualBillingBannerQuery = gql`
  query annualBillingBanner($accountId: Int!) {
    account(id: $accountId) {
      currentUserRole
      firstKnownUpgradeDate
      id
      plan {
        amount
        billingInterval
        id
        name
        type
      }
    }

    planStacks: pricingPageV3Plans(accountId: $accountId) {
      plans {
        amount
        id
        interval
        name
      }
      planType
    }
  }
`;

export type Props = {
  skip?: boolean;
};

export const useData = ({ skip }: Props = {}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const accountId = localStorage.getItem('currentAccountId');

  const { data, loading, error } = useQuery<AnnualBillingBannerQuery>(
    annualBillingBannerQuery,
    {
      skip: skip || !isAuthenticated || !accountId,
      variables: {
        accountId: parseInt(accountId ?? ''),
      },
    }
  );

  const account = data?.account;
  const currentPlan = account?.plan;

  const currentPlanAlternate = data?.planStacks
    ?.find(planStack => planStack.planType === currentPlan?.type)
    ?.plans?.find(
      plan =>
        plan.name === currentPlan?.name &&
        plan.interval !== currentPlan?.billingInterval
    );

  let yearlyAmount = 0;
  let monthlyAmount = 0;

  if (currentPlan?.billingInterval === 'year') {
    yearlyAmount = currentPlan?.amount ?? 0;
    monthlyAmount = currentPlanAlternate?.amount ?? 0;
  } else {
    yearlyAmount = currentPlanAlternate?.amount ?? 0;
    monthlyAmount = currentPlan?.amount ?? 0;
  }

  const yearlyDiscountPercentage = calculateDiscount(
    yearlyAmount,
    monthlyAmount
  );

  return {
    currentPlanName: currentPlan?.name,
    error: error
      ? 'There was an error fetching account information. Please try again.'
      : null,
    shouldRender: isEligibleForAnnualUpgrade({
      accountAgeDays: account?.firstKnownUpgradeDate
        ? daysFromNow(account?.firstKnownUpgradeDate)
        : undefined,
      currentPlanInterval: currentPlan?.billingInterval,
      currentUserRole: account?.currentUserRole,
      yearlyDiscountPercentage,
    }),
    isLoading: loading,
    yearlyDiscountPercentage,
  };
};
